import type { IStage } from '@metservicedev/platforms-services-interface';
import type { IMetjwtClient, IMetjwtUser } from '@metservicedev/platforms-services-interface/src/services/authproviderwrapper/AuthproviderwrapperModel';
import type { IOrganisation, IPermission } from '@mip/shared';
import { usePlatStageContext } from '@mono/use-plat-stage';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const getMetjwt = () => {
    try {
        const search = window.location.search;
        if (search.includes('?jwt=')) {
            const metjwt = search.replace('?jwt=', '');
            window.localStorage.metjwt = metjwt;
            window.location.href = window.location.origin + window.location.pathname;

            return metjwt;
        }
        const metjwt: string = window.localStorage.metjwt;
        const { exp } = JSON.parse(atob(metjwt.split('.')[1]));
        const isExpired = new Date().valueOf() > exp * 1000;
        if (isExpired) {
            window.localStorage.removeItem('metjwt');
        }
        return !isExpired ? metjwt : undefined;
    } catch (e) {
        return undefined;
    }
};

const getUser = () => {
    const metjwt = getMetjwt();
    if (metjwt === undefined) return metjwt;
    return JSON.parse(atob(metjwt.split('.')[1])) as IMetjwtUser;
};

export const AuthContext = createContext<{
    metjwt?: string;
    clients: IMetjwtClient[];
    permissions: IPermission[] | null;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    reloadMetjwt: () => Promise<void>;
    userState: 'loading' | 'logging-in' | 'logging-out' | 'switching-client' | 'interval';
    user?: IMetjwtUser;
    movBaseDomain: string;
    switchClient: (clientId: string) => void;
    stage: IStage;
    error?: string;
    parsedMetjwt?: IMetjwtUser;
    currentOrg: any | null;
}>({
    metjwt: getMetjwt(),
    clients: [],
    permissions: [],
    login: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    reloadMetjwt: () => Promise.resolve(),
    userState: 'interval',
    user: undefined,
    movBaseDomain: '',
    switchClient: (_clientId) => false,
    stage: 'test',
    parsedMetjwt: undefined,
    currentOrg: null,
});

const baseDomains = {
    test: 'https://test-insights.metservice.com',
    acceptance: 'https://uat-insights.metservice.com',
    prod: 'https://insights.metservice.com',
};

const getAuthToken = () => {
    let metjwt: string | undefined;
    try {
        metjwt = localStorage.metjwt;
    } catch (e) {
        throw e;
    }

    if (metjwt) {
        const { exp } = JSON.parse(atob(metjwt.split('.')[1]));
        const isExpired = new Date().valueOf() > new Date(exp * 1000).valueOf();
        if (isExpired) {
            window.localStorage.removeItem('metjwt');
            window.localStorage.removeItem('jwt');
            throw new Error('Expired auth token');
        } else {
            return metjwt;
        }
    }
    throw new Error('No auth token');
};

export const AuthContextProvider = ({
    children,
    reloadOnMetjwtChange = true,
}: {
    children: JSX.Element;
    reloadOnMetjwtChange?: boolean;

    //Some apps are not purely react based and they take metjwt from localStorage directly. We need to reload the page to ensure those apps works correctly
}) => {
    const { stage } = usePlatStageContext();
    const [metjwt, _setMetjwt] = useState(getMetjwt());
    const [clients, setClients] = useState<IMetjwtClient[]>([]);
    const [permissions, setPermissions] = useState<IPermission[] | null>(null);
    const [currentOrg, setCurrentOrg] = useState<IOrganisation | null>(null);
    const [user, setUser] = useState<IMetjwtUser | undefined>(getUser());
    const [error, setError] = useState<string>();
    const [userState, setUserState] = useState<'loading' | 'logging-in' | 'logging-out' | 'switching-client' | 'interval'>('interval');
    const movBaseDomain = baseDomains[stage];

    const parsedMetjwt = useMemo(() => (metjwt ? JSON.parse(atob(metjwt.split('.')[1])) : undefined), [metjwt]);
    const MipApiStage = stage === 'prod' ? '' : stage === 'acceptance' ? 'uat-' : `${stage}-`;

    const accessToken = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}').accessToken?.accessToken;
    const headers = new Headers();
    headers.append('Authorization', `Bearer ${accessToken}`);
    headers.append('X-Current-Org', localStorage.getItem('mip-organisation-id') ?? '');

    useEffect(() => {
        if (window.location.hostname === 'localhost') return;
        if (metjwt) return;
        login();
    }, []);

    useEffect(() => {
        (async () => {
            if (!metjwt) {
                setPermissions(null);
                setClients([]);
                return;
            }
            setUserState('loading');

            const [initResponse, clientsResponse] = await Promise.all([
                fetch(`https://api.${MipApiStage}insights.metservice.com/users/init`, { headers }).then((res) => res.json()),
                fetch(`https://api.${MipApiStage}insights.metservice.com/users/${parsedMetjwt.userId}/organisations`, { headers }).then((res) => res.json()),
            ]).catch(async (e) => {
                console.log('error', e);
                await logout();
                throw e;
            });

            if (initResponse.error || clientsResponse.error) {
                console.error(initResponse.error || clientsResponse.error);
                await logout();
                return;
            }

            const { permissions, currentOrganisation } = initResponse.response;

            setPermissions(permissions);
            setCurrentOrg(currentOrganisation);
            setClients(clientsResponse.response);
            setUserState('interval');
        })();
        setUser(getUser());
    }, [metjwt]);

    const setMetjwt = (newMetjwt: string) => {
        window.localStorage.setItem('metjwt', newMetjwt);
        window.localStorage.removeItem('jwt');

        if (reloadOnMetjwtChange) {
            window.location.reload();
            return;
        }
        _setMetjwt(newMetjwt);
    };

    const unsetMetjwt = () => {
        window.localStorage.removeItem('metjwt');
        window.localStorage.removeItem('jwt');
        _setMetjwt(undefined);
    };

    const removeMIPDetails = () => {
        window.localStorage.removeItem('mip-user-details');
    };

    const login = async () => {
        setUserState('logging-in');
        try {
            const authToken = getAuthToken();
            console.log('authToken', authToken);
            if (authToken) {
                setMetjwt(authToken);
            } else {
                unsetMetjwt();
                throw new Error('authToken was empty');
            }
            setUserState('interval');
            setError(undefined);
            !!reloadOnMetjwtChange && window.location.reload();
        } catch (e) {
            console.log('error', e);
            window.location.href = `${movBaseDomain}/auth/signin?redirectUri=${encodeURIComponent(window.location.pathname)}`;
        }
    };

    const logout = async () => {
        setUserState('logging-out');
        unsetMetjwt();
        removeMIPDetails();
        window.location.href = `${movBaseDomain}/auth/signin?redirectUri=${encodeURIComponent(window.location.pathname)}`;
    };

    const switchClient = async (clientId: string) => {
        setUserState('switching-client');
        setPermissions(null);
        setClients([]);

        console.log(`switching to: ${clientId}`);
        localStorage.setItem('mip-organisation-id', clientId);
        headers.set('X-Current-Org', clientId);

        // Clear recent reports from local storage as the next organisation may not have access to the same reports
        localStorage.removeItem('recent-reports');

        //Switching client means getting a new metjwt that has a different primary client, acting as a different client.
        const response = await fetch(
            `https://api.${stage === 'prod' ? '' : stage === 'acceptance' ? 'uat-' : `${stage}-`}insights.metservice.com/users/metjwt`,
            {
                headers,
            }
        ).then((res) => res.json());

        const newMetjwt = response.response.token;
        setUserState('interval');
        setMetjwt(newMetjwt);
    };

    const reloadMetjwt = async () => {
        setUserState('logging-in');
        if (!user?.clientId || !metjwt) {
            console.error('You cannot reload a metjwt because you are not logged in. Redirecting to login instead');
            return login();
        }
        const response = await fetch(
            `https://api.${stage === 'prod' ? '' : stage === 'acceptance' ? 'uat-' : `${stage}-`}insights.metservice.com/users/metjwt`,
            {
                headers,
            }
        ).then((res) => res.json());
        const newMetjwt = response.response.token;
        setMetjwt(newMetjwt);
        setUserState('interval');
    };

    return (
        <AuthContext.Provider
            value={{
                stage,
                metjwt,
                clients,
                permissions,
                currentOrg,
                login,
                logout,
                userState,
                user,
                movBaseDomain,
                switchClient,
                reloadMetjwt,
                error,
                parsedMetjwt,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => useContext(AuthContext);
